import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  useTheme as muiUseTheme,
  useMediaQuery,
  Skeleton,
  Chip,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/apiService';

const SPORT_COLORS = {
  baseball: '#1976d2',
  basketball: '#ff6b35',
  football: '#4caf50',
};

interface SportSummary {
  name: string;
  color: string;
  icon: React.ReactElement;
  propCount: number | null;
  gameCount: number | null;
  contestCount: number | null;
  lineupCount: number | null;
  bettingPath: string;
  dfsPath: string;
  loading: boolean;
  error: boolean;
}

interface ResultsSummary {
  sport: string;
  color: string;
  totalProps: number;
  hits: number;
  hitRate: number;
  date: string;
}

const StatValue: React.FC<{ value: number | null; loading: boolean }> = ({ value, loading }) => {
  if (loading) return <Skeleton width={40} height={28} sx={{ display: 'inline-block' }} />;
  if (value === null) return <Typography component="span" color="text.disabled">-</Typography>;
  return <>{value.toLocaleString()}</>;
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const muiTheme = muiUseTheme();
  const { isDarkMode } = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [sports, setSports] = useState<SportSummary[]>([
    { name: 'Baseball', color: SPORT_COLORS.baseball, icon: <SportsBaseballIcon />, propCount: null, gameCount: null, contestCount: null, lineupCount: null, bettingPath: '/baseball/betting', dfsPath: '/baseball/dfs', loading: true, error: false },
    { name: 'Basketball', color: SPORT_COLORS.basketball, icon: <SportsBasketballIcon />, propCount: null, gameCount: null, contestCount: null, lineupCount: null, bettingPath: '/basketball/betting', dfsPath: '/basketball/dfs', loading: true, error: false },
    { name: 'Football', color: SPORT_COLORS.football, icon: <SportsFootballIcon />, propCount: null, gameCount: null, contestCount: null, lineupCount: null, bettingPath: '/football/betting', dfsPath: '/football/dfs', loading: true, error: false },
  ]);

  const [results, setResults] = useState<ResultsSummary[]>([]);
  const [resultsLoading, setResultsLoading] = useState(true);

  const updateSport = (name: string, updates: Partial<SportSummary>) => {
    setSports(prev => prev.map(s => s.name === name ? { ...s, ...updates } : s));
  };

  const fetchAll = useCallback(async () => {
    // Sport data — single lightweight endpoint for all counts
    apiService.getDashboardStats().then((stats) => {
      updateSport('Baseball', { propCount: stats.baseball.propCount, gameCount: stats.baseball.gameCount, contestCount: stats.baseball.contestCount, lineupCount: stats.baseball.lineupCount, loading: false });
      updateSport('Basketball', { propCount: stats.basketball.propCount, gameCount: stats.basketball.gameCount, contestCount: stats.basketball.contestCount, lineupCount: stats.basketball.lineupCount, loading: false });
      updateSport('Football', { propCount: stats.football.propCount, gameCount: stats.football.gameCount, contestCount: stats.football.contestCount, lineupCount: stats.football.lineupCount, loading: false });
    }).catch(() => {
      updateSport('Baseball', { loading: false, error: true });
      updateSport('Basketball', { loading: false, error: true });
      updateSport('Football', { loading: false, error: true });
    });

    // Results — API returns { propResults: { total, hits }, date, ... }
    Promise.all([
      apiService.getBaseballPropResultsSummary().catch(() => null),
      apiService.getPropResultsSummary().catch(() => null),
      apiService.getFootballPropResultsSummary().catch(() => null),
    ]).then(([baseballRes, basketballRes, footballRes]) => {
      const summaries: ResultsSummary[] = [];
      const bb = baseballRes?.propResults;
      if (bb?.total > 0) {
        const hitRate = bb.total > 0 ? (bb.hits / bb.total) * 100 : 0;
        summaries.push({ sport: 'Baseball', color: SPORT_COLORS.baseball, totalProps: bb.total, hits: bb.hits, hitRate, date: baseballRes.date ?? 'Recent' });
      }
      const bk = basketballRes?.propResults;
      if (bk?.total > 0) {
        const hitRate = bk.total > 0 ? (bk.hits / bk.total) * 100 : 0;
        summaries.push({ sport: 'Basketball', color: SPORT_COLORS.basketball, totalProps: bk.total, hits: bk.hits, hitRate, date: basketballRes.date ?? 'Recent' });
      }
      const fb = footballRes?.propResults;
      if (fb?.total > 0) {
        const hitRate = fb.total > 0 ? (fb.hits / fb.total) * 100 : 0;
        summaries.push({ sport: 'Football', color: SPORT_COLORS.football, totalProps: fb.total, hits: fb.hits, hitRate, date: footballRes.date ?? 'Recent' });
      }
      setResults(summaries);
      setResultsLoading(false);
    }).catch(() => setResultsLoading(false));

  }, []);

  useEffect(() => {
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 3 }}>
      {/* Sport Activity Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: isMobile ? 2 : 3,
          mb: 3,
        }}
      >
        {sports.map((sport) => (
          <Card key={sport.name} sx={{ borderRadius: 2, position: 'relative', overflow: 'visible' }}>
            <CardContent sx={{ px: isMobile ? 2 : 2.5, py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{ color: sport.color, display: 'flex', alignItems: 'center' }}>
                  {React.cloneElement(sport.icon, { sx: { fontSize: 24 } })}
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {sport.name}
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Props</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    <StatValue value={sport.propCount} loading={sport.loading} />
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Games</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    <StatValue value={sport.gameCount} loading={sport.loading} />
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Contests</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    <StatValue value={sport.contestCount} loading={sport.loading} />
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Lineups</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    <StatValue value={sport.lineupCount} loading={sport.loading} />
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Chip
                  icon={<SportsSoccerIcon sx={{ fontSize: '1rem !important' }} />}
                  label="Betting"
                  onClick={() => navigate(sport.bettingPath)}
                  sx={{ flex: 1, height: 40, cursor: 'pointer', borderColor: alpha(sport.color, 0.5), color: sport.color, backgroundColor: alpha(sport.color, 0.05), '&:hover': { backgroundColor: alpha(sport.color, 0.12) }, fontSize: '0.85rem', fontWeight: 500 }}
                  variant="outlined"
                />
                <Chip
                  icon={<ViewListIcon sx={{ fontSize: '1rem !important' }} />}
                  label="DFS"
                  onClick={() => navigate(sport.dfsPath)}
                  sx={{ flex: 1, height: 40, cursor: 'pointer', borderColor: alpha(sport.color, 0.5), color: sport.color, backgroundColor: alpha(sport.color, 0.05), '&:hover': { backgroundColor: alpha(sport.color, 0.12) }, fontSize: '0.85rem', fontWeight: 500 }}
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Recent Results */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ px: isMobile ? 2 : 3, py: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
            Latest Results
          </Typography>
          {resultsLoading ? (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" width={180} height={60} />)}
            </Box>
          ) : results.length === 0 ? (
            <Typography variant="body2" color="text.disabled">No recent results available</Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: `repeat(${results.length}, 1fr)` },
                gap: 2,
              }}
            >
              {results.map((r) => (
                <Box
                  key={r.sport}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    backgroundColor: alpha(r.color, isDarkMode ? 0.08 : 0.04),
                    border: `1px solid ${alpha(r.color, 0.15)}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: r.color }}>
                      {r.sport}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{r.date}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: r.hitRate >= 50 ? '#4caf50' : '#f44336' }}>
                      {Math.round(r.hitRate)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {r.hits}/{r.totalProps} props
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default HomePage;
